import { useEffect } from 'react'
import { useToast } from '@ui/toasts/useToastManager'
import { Interest, Question } from '@ca/common/types'
import { trpc } from 'api'
import { useConfigCondition } from 'features/config/useConfigCondition'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type AnswerState = {
  state: boolean
  no_answer: boolean
}

const getValue = (answer: string): AnswerState => {
  if (answer === 'yes') {
    return {
      state: true,
      no_answer: false,
    }
  }
  if (answer === 'no') {
    return {
      state: false,
      no_answer: false,
    }
  }

  return {
    state: false,
    no_answer: true,
  }
}

const getDefaultValue = (item?: Interest) => {
  if (!item) {
    return ''
  }
  if (item.no_answer) {
    return 'na'
  }

  return item.state ? 'yes' : 'no'
}

interface UseSurveyFormProps {
  showAgainIfFilledBefore?: boolean
}

/**
 * @param showAgainIfFilledBefore - If true, the survey will be shown again if the user has already filled the survey before.
 */
const useSurveyForm = (
  { showAgainIfFilledBefore }: UseSurveyFormProps = {
    showAgainIfFilledBefore: false,
  }
) => {
  const config = useRemoteConfig()
  const questions = config?.questions
  const enabled = !!useConfigCondition('survey:enabled')
  const toast = useToast()
  const { t } = useTranslation(['toasts'])

  const { data: interests } = trpc.customer.interests.get.useQuery(undefined, {
    enabled,
  })

  const { mutate, isPending, isSuccess } =
    trpc.customer.interests.save.useMutation({
      onError: (err) => {
        console.error(err)
        toast({
          title: t('toasts:error.default.title'),
          text: err.message ?? t('toasts:error.default.desc'),
          variant: 'error',
        })
      },
    })

  const hasAllAnswers = useMemo(() => {
    if (!interests || !questions) {
      return false
    }
    return questions.every(
      (question) => interests[question.code]?.no_answer === false
    )
  }, [questions, interests])

  const show =
    enabled &&
    questions?.length &&
    (showAgainIfFilledBefore ? true : !hasAllAnswers)

  const defaultValues: Record<string, string> = useMemo(() => {
    if (!questions) {
      return {}
    }
    const entries = questions?.map((question) => [
      question.code,
      getDefaultValue(interests?.[question.code]),
    ])
    return Object.fromEntries(entries) satisfies Record<string, string>
  }, [questions, interests])

  const form = useForm(defaultValues)

  // Reset form values when defaultValues change (e.g., after async load)
  useEffect(() => {
    if (
      defaultValues &&
      Object.keys(defaultValues).length > 0 &&
      !form.formState.isDirty
    ) {
      form.reset(defaultValues)
    }
  }, [defaultValues, form])

  const onSubmit = form.handleSubmit((data) => {
    // Should never happen, makes
    if (!questions) {
      return
    }
    const answers = questions?.map((question) => {
      const value = getValue(data[question.code])
      return {
        name: question.name,
        code: question.code,
        ...value,
      }
    })
    mutate({ answers })
  })

  return {
    form,
    isPending,
    isSubmitted: isSuccess,
    onSubmit,
    questions,
    show,
    defaultValues,
  }
}

export default useSurveyForm
